import { useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "@/config/react-query";

import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";

import { useToast } from "@/lib/use-toast";

import {
  Tenant,
  TenantBookingFeeCollectionUpdate,
  TenantSecuritySettingsUpdate,
  TenantSelect,
  TenantSelfSchedulingUpdate,
  TenantShopPresentationUpdate,
  TenantUpdate,
} from "@/types/tenant";

import { requestData } from "@/api/api-config";
import {
  fetchEnterpriseTenant,
  fetchPrivateTenant,
  fetchTenant,
  fetchTenantHasParser,
  fetchTenantLogo,
  listApiKeys,
  updateBookingFeeCollection,
  updateSecuritySettings,
  updateSelfScheduling,
  updateShopPresentation,
  updateTenant,
  uploadReceiptLogo,
  uploadTenantLogo,
} from "@/api/tenant";

export const tenantQueryKeys = {
  all: ["tenant"] as const,
  available: (firebaseUid: string) =>
    [...tenantQueryKeys.all, "select", firebaseUid] as const,
  public: (tenantName?: string) =>
    [...tenantQueryKeys.all, "public", tenantName] as const,
  private: () => [...tenantQueryKeys.all, "private"] as const,
  parser: () => [...tenantQueryKeys.all, "parser"] as const,
  logo: () => [...tenantQueryKeys.all, "logo"] as const,
  enterprise: () => [...tenantQueryKeys.all, "enterprise"] as const,
  apiKeys: () => [...tenantQueryKeys.all, "api-keys"] as const,
};

async function fetchAvailableTenants(): Promise<TenantSelect[] | null> {
  return requestData<TenantSelect[]>("tenant/select", "get");
}

export const tenantQueryOptions = {
  public: (tenantName?: string) => ({
    queryKey: tenantQueryKeys.public(tenantName),
    queryFn: () => fetchTenant(tenantName),
  }),
  private: () => ({
    queryKey: tenantQueryKeys.private(),
    queryFn: () => fetchPrivateTenant(),
  }),
  parser: () => ({
    queryKey: tenantQueryKeys.parser(),
    queryFn: () => fetchTenantHasParser(),
  }),
  logo: () => ({
    queryKey: tenantQueryKeys.logo(),
    queryFn: () => fetchTenantLogo(),
  }),
  enterprise: () => ({
    queryKey: tenantQueryKeys.enterprise(),
    queryFn: () => fetchEnterpriseTenant(),
  }),
  apiKeys: () => ({
    queryKey: tenantQueryKeys.apiKeys(),
    queryFn: () => listApiKeys(),
  }),
};

async function refreshPrivateTenant(
  refetchTenant: () => Promise<Awaited<ReturnType<typeof fetchPrivateTenant>>>,
) {
  const newTenant = await refetchTenant();
  if (newTenant) {
    queryClient.setQueryData(tenantQueryKeys.private(), newTenant);
  }
  return newTenant;
}

export const updateTenantMutationKey = [
  ...tenantQueryKeys.all,
  "update",
] as const;

export function useUpdateTenantMutation() {
  const { refetchTenant } = useTenant();
  const { toast } = useToast();

  return useMutation({
    mutationKey: updateTenantMutationKey,
    mutationFn: (tenantUpdate: TenantUpdate) => updateTenant(tenantUpdate),
    onSuccess: async (result) => {
      if (!result) return;
      const newTenant = await refreshPrivateTenant(refetchTenant);
      if (!newTenant) {
        toast({
          title: "Saved",
          description: "Settings saved. Reload to see updated values.",
          variant: "warning",
        });
      }
    },
    onError: (error) => {
      console.error("Tenant update error:", error);
    },
  });
}

/**
 * Security settings and shop presentation post to their own endpoints (each
 * permissioned for its own settings surface) but refresh the tenant the same way.
 */
function useTenantFieldMutation<TPayload>(
  mutationFn: (payload: TPayload) => Promise<Tenant | null>,
  errorLabel: string,
) {
  const { refetchTenant } = useTenant();
  const { toast } = useToast();

  return useMutation({
    mutationFn,
    onSuccess: async (result) => {
      if (!result) return;
      const newTenant = await refreshPrivateTenant(refetchTenant);
      if (!newTenant) {
        toast({
          title: "Saved",
          description: "Settings saved. Reload to see updated values.",
          variant: "warning",
        });
      }
    },
    onError: (error) => {
      console.error(errorLabel, error);
    },
  });
}

export function useUpdateSecuritySettingsMutation() {
  return useTenantFieldMutation(
    (update: TenantSecuritySettingsUpdate) => updateSecuritySettings(update),
    "Security settings update error:",
  );
}

export function useUpdateSelfSchedulingMutation() {
  return useTenantFieldMutation(
    (update: TenantSelfSchedulingUpdate) => updateSelfScheduling(update),
    "Self-scheduling update error:",
  );
}

export function useUpdateBookingFeeCollectionMutation() {
  return useTenantFieldMutation(
    (update: TenantBookingFeeCollectionUpdate) =>
      updateBookingFeeCollection(update),
    "Booking fee collection update error:",
  );
}

export function useUpdateShopPresentationMutation() {
  return useTenantFieldMutation(
    (update: TenantShopPresentationUpdate) => updateShopPresentation(update),
    "Shop presentation update error:",
  );
}

export function useUploadTenantLogoMutation() {
  const { refetchTenant } = useTenant();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (file: File) => uploadTenantLogo(file),
    onSuccess: async (logoUrl) => {
      if (!logoUrl) {
        toast({
          title: "Logo Upload Failed",
          description: "No URL returned from upload.",
          variant: "error",
        });
        return;
      }
      const newTenant = await refreshPrivateTenant(refetchTenant);
      if (!newTenant) {
        toast({
          title: "Logo Uploaded",
          description: "Logo saved. Reload to see updated values.",
          variant: "warning",
        });
      }
    },
    onError: (error) => {
      console.error("Logo upload error:", error);
      toast({
        title: "Logo Upload Failed",
        description: "Unable to upload logo. Please try again.",
        variant: "error",
      });
    },
  });
}

export function useUploadReceiptLogoMutation() {
  const { refetchTenant } = useTenant();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (file: File) => uploadReceiptLogo(file),
    onSuccess: async (receiptLogoUrl) => {
      if (!receiptLogoUrl) {
        toast({
          title: "Receipt Logo Upload Failed",
          description: "No URL returned from upload.",
          variant: "error",
        });
        return;
      }
      const newTenant = await refreshPrivateTenant(refetchTenant);
      if (!newTenant) {
        toast({
          title: "Receipt Logo Uploaded",
          description: "Logo saved. Reload to see updated values.",
          variant: "warning",
        });
      }
    },
    onError: (error) => {
      console.error("Receipt logo upload error:", error);
      toast({
        title: "Receipt Logo Upload Failed",
        description: "Unable to upload receipt logo. Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Shared React Query hook for available tenants. Deduplicates requests across
 * ProfileButton and NavigationSearch so only one API call is made.
 */
export function useAvailableTenants(options?: { enabled?: boolean }) {
  const { currentUser } = useUser();
  const firebaseUid = currentUser?.firebaseUid ?? "";
  const enabled = (options?.enabled ?? true) && !!firebaseUid;

  return useQuery({
    queryKey: tenantQueryKeys.available(firebaseUid),
    queryFn: fetchAvailableTenants,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
